import { useEffect } from "react";
import { IControlledMutator } from "sonobello.utilities.react";
import { Nullable } from "sonobello.utilities.react.mui";

import { EnhancedCenter } from "../../../Models/Center";
import UserContextValueProvider from "../../Providers/Value/UserContextValueProvider";
import LoadingForm from "../Components/AuthenticationForms/LoadingForm";
import { ILoginStepProps } from "../Components/LoginProcess";
import useGetLoginDependencies from "../Hooks/UseGetLoginDependencies";
import { LoginDependencies } from "../Types/LoginDependencies";
import BaseGemAuthenticationCard from "./BaseGemAuthenticationCard";

interface IDependenciesLoadingCardProps extends IControlledMutator<Nullable<LoginDependencies>> {
  isAuthReady: boolean;
}

const LoginTypeLoadingCardContent = BaseGemAuthenticationCard({
  AuthenticationForm: LoadingForm,
  salutation: "Loading Server Assets"
});

const DependenciesLoadingCard: React.FC<IDependenciesLoadingCardProps & ILoginStepProps> = ({
  isAuthReady,
  value,
  onChange,
  onComplete
}) => {
  const { dependencies, execute } = useGetLoginDependencies();

  useEffect(() => {
    if (isAuthReady) execute();
  }, [isAuthReady]);

  useEffect(() => {
    if (dependencies) onChange(dependencies);
  }, [dependencies]);

  useEffect(() => {
    if (value) onComplete();
  }, [value]);

  return LoginTypeLoadingCardContent;
};

const UserContextDependenciesLoadingCard: React.FC<ILoginStepProps> = UserContextValueProvider<
  IDependenciesLoadingCardProps,
  IDependenciesLoadingCardProps & ILoginStepProps
>({
  Component: DependenciesLoadingCard,
  map: ({
    areasOfInterest,
    businessUnits,
    centers,
    disqualifyingReasons,
    dynamicContent,
    leadResultTypes,
    notOfferedProcedures,
    token,
    setAreasOfInterest,
    setBusinessUnits,
    setCenters,
    setDisqualifyingReasons,
    setDynamicContent,
    setLeadResultTypes,
    setNotOfferedProcedures
  }) => {
    const value =
      areasOfInterest &&
      businessUnits &&
      centers &&
      disqualifyingReasons &&
      dynamicContent &&
      leadResultTypes &&
      notOfferedProcedures
        ? {
            areasOfInterest,
            businessUnits,
            centers,
            disqualifyingReasons,
            dynamicContent,
            leadResultTypes,
            notOfferedProcedures
          }
        : null;

    return {
      isAuthReady: Boolean(token),
      value,
      onChange: deps => {
        if (!deps) throw "Cannot unset dependencies.";
        setAreasOfInterest(deps.areasOfInterest);
        setBusinessUnits(deps.businessUnits);
        setCenters(deps.centers.map(c => new EnhancedCenter(c)));
        setDisqualifyingReasons(deps.disqualifyingReasons);
        setDynamicContent(deps.dynamicContent);
        setLeadResultTypes(deps.leadResultTypes);
        setNotOfferedProcedures(deps.notOfferedProcedures);
      }
    };
  }
});

export default UserContextDependenciesLoadingCard;
